/**
 * DASNA design tokens — dark / light + brand accents.
 * Spacing scale: 8 / 12 / 16 / 24 / 32
 */
export const space = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
};

/** Brand accents for SNA / Prolife / PLAS chips & tabs */
export const brandColors = {
  sna: '#22d3ee',
  prolife: '#34d399',
  plas: '#f59e0b',
  lift: '#38bdf8',
  resaltys: '#a3e635',
  skinglow: '#fb7185',
};

export const darkTheme = {
  mode: 'dark',
  bg: '#080c16',
  bgCard: '#111827',
  bgSidebar: '#0c1220',
  bgInput: '#1a2332',
  border: '#1e2a3a',
  borderHover: '#2e4058',
  accent: '#22d3ee',
  accentHover: '#06b6d4',
  accentDim: 'rgba(34,211,238,0.12)',
  textPrimary: '#e8edf4',
  textSecondary: '#8b949e',
  textMuted: '#5c6674',
  success: '#4ade80',
  warning: '#fbbf24',
  danger: '#f87171',
  chartColors: ['#22d3ee', '#34d399', '#f59e0b', '#fb7185', '#38bdf8', '#a3e635', '#818cf8', '#fb923c'],
  shadowSm: '0 2px 10px rgba(0,0,0,.22)',
  shadowMd: '0 6px 20px rgba(0,0,0,.28)',
  shadowLg: '0 12px 36px rgba(0,0,0,.36)',
  cardRest: '0 0 0 1px rgba(30,42,58,.9), 0 4px 16px rgba(0,0,0,.22)',
  cardHover: '0 0 0 1px rgba(34,211,238,.2), 0 8px 24px rgba(0,0,0,.3)',
  mainBackdrop:
    'radial-gradient(ellipse 100% 70% at 50% -25%, rgba(34,211,238,.07) 0%, transparent 52%), radial-gradient(ellipse 55% 40% at 100% 0%, rgba(52,211,153,.04) 0%, transparent 50%)',
  surfaceSheen:
    'linear-gradient(145deg, rgba(255,255,255,.035) 0%, transparent 40%, rgba(34,211,238,.025) 100%)',
  fontDisplay: "'Sora', 'DM Sans', system-ui, sans-serif",
  fontBody: "'DM Sans', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', 'DM Mono', ui-monospace, monospace",
  space,
  brand: brandColors,
};

export const lightTheme = {
  mode: 'light',
  bg: '#f4f7fb',
  bgCard: '#ffffff',
  bgSidebar: '#eef2f7',
  bgInput: '#ffffff',
  border: '#d8e0ea',
  borderHover: '#b8c5d6',
  accent: '#0891b2',
  accentHover: '#0e7490',
  accentDim: 'rgba(8,145,178,0.1)',
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#64748b',
  success: '#16a34a',
  warning: '#d97706',
  danger: '#dc2626',
  chartColors: ['#0891b2', '#059669', '#d97706', '#e11d48', '#0284c7', '#65a30d', '#6366f1', '#ea580c'],
  shadowSm: '0 1px 4px rgba(15,23,42,.06)',
  shadowMd: '0 4px 14px rgba(15,23,42,.08)',
  shadowLg: '0 10px 28px rgba(15,23,42,.1)',
  cardRest: '0 0 0 1px rgba(216,224,234,.95), 0 2px 10px rgba(15,23,42,.05)',
  cardHover: '0 0 0 1px rgba(8,145,178,.25), 0 6px 18px rgba(15,23,42,.08)',
  mainBackdrop:
    'radial-gradient(ellipse 100% 70% at 50% -20%, rgba(8,145,178,.08) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 100% 0%, rgba(5,150,105,.05) 0%, transparent 48%)',
  surfaceSheen:
    'linear-gradient(145deg, rgba(255,255,255,.9) 0%, transparent 42%, rgba(8,145,178,.03) 100%)',
  fontDisplay: "'Sora', 'DM Sans', system-ui, sans-serif",
  fontBody: "'DM Sans', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', 'DM Mono', ui-monospace, monospace",
  space,
  brand: brandColors,
};

/** Apply CSS custom properties for global styles / light-dark without rewriting every page */
export function applyThemeVars(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme.mode);
  const map = {
    '--bg': theme.bg,
    '--bg-card': theme.bgCard,
    '--bg-sidebar': theme.bgSidebar,
    '--bg-input': theme.bgInput,
    '--border': theme.border,
    '--border-hover': theme.borderHover,
    '--accent': theme.accent,
    '--accent-hover': theme.accentHover,
    '--accent-dim': theme.accentDim,
    '--text-primary': theme.textPrimary,
    '--text-secondary': theme.textSecondary,
    '--text-muted': theme.textMuted,
    '--success': theme.success,
    '--warning': theme.warning,
    '--danger': theme.danger,
    '--card-rest': theme.cardRest,
    '--card-hover': theme.cardHover,
    '--main-backdrop': theme.mainBackdrop,
    '--surface-sheen': theme.surfaceSheen,
    '--shadow-sm': theme.shadowSm,
    '--shadow-md': theme.shadowMd,
    '--shadow-lg': theme.shadowLg,
    '--color-scheme': theme.mode,
    '--font-display': theme.fontDisplay,
    '--font-body': theme.fontBody,
    '--font-mono': theme.fontMono,
    '--space-xs': `${space.xs}px`,
    '--space-sm': `${space.sm}px`,
    '--space-md': `${space.md}px`,
    '--space-lg': `${space.lg}px`,
    '--space-xl': `${space.xl}px`,
  };
  Object.entries(map).forEach(([k, v]) => root.style.setProperty(k, v));
  document.body.style.background = theme.bg;
  document.body.style.color = theme.textPrimary;
  document.body.style.colorScheme = theme.mode;
}

/** @deprecated use ThemeContext — kept for backward-compatible imports */
const theme = darkTheme;
export default theme;
